import type {
  PortalBucketCoverage,
  PortalCoverage,
  PortalGrouping,
  PortalSeriesPoint,
  PortalTotals,
} from '../../types/portalReport.js';

// CLIENT PORTAL Phase 1 — the report's pure series core. No I/O, no formatting
// beyond bucket labels, exhaustively tested.
//
// ⚠️⚠️ THE RULE THIS FILE EXISTS FOR: an UNMEASURED bucket is not a zero.
//
// Reporting backfill is tiered — some accounts hold ~25 months of history,
// others only the recent 90 days (see the Coverage page). On an internal
// dashboard a missing month is a mild annoyance. On a CLIENT'S report it is a
// lie: a bar of height zero, sitting beside twelve real ones, tells a business
// owner their advertising produced nothing that month. The truth is that we
// were not measuring yet.
//
// So an unmeasured bucket carries `leads: null, spend: null, costPerLead: null`
// and `coverage: 'no-data'`, and the chart draws a labelled gap. Nothing in this
// file may substitute 0 for null on that path — it is mutation-tested from both
// directions.

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** One measured bucket as it arrives from a query: always real numbers. */
export interface MeasuredBucket {
  bucket: string;
  leads: number;
  spend: number;
  /**
   * ⚠️ Delivery counts. REQUIRED, not optional — every construction site has to
   * declare them, so a query that forgets to select them fails to compile rather
   * than quietly producing a flat zero line on a client's report.
   */
  impressions: number;
  clicks: number;
}

// ---------------------------------------------------------------------------
// Bucket keys + labels
// ---------------------------------------------------------------------------

/** 'YYYY-MM-DD' → 'YYYY-MM'. Pure string work; no Date, no timezone. */
export function monthKeyOf(dateYmd: string): string {
  return dateYmd.slice(0, 7);
}

export function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-');
  const idx = Number(m) - 1;
  return `${MONTH_NAMES[idx] ?? m} ${y}`;
}

/**
 * A month for an AXIS TICK: `Mar ’26`.
 *
 * ⚠️⚠️ A SEPARATE FUNCTION FROM `monthLabel`, AND THAT IS THE WHOLE POINT.
 * `monthLabel` is NOT only an axis formatter — `coverageNote` renders it inside
 * PROSE: *"Reporting for this account starts in March 2026."* Shortening
 * `monthLabel` to fix an axis would produce *"…starts in Mar ’26"* in a
 * sentence, which reads as a typo in the one place the report is explaining
 * itself. The two callers want different registers and one function cannot
 * serve both.
 *
 * ⚠️ THE APOSTROPHE GOES BEFORE THE DIGITS and it is a RIGHT SINGLE QUOTE
 * (U+2019), not a straight tick: `Mar 26'` reads as a stray quotation mark,
 * `Mar ’26` reads as a year.
 *
 * ⚠️ An unrecognised month falls back to the raw part rather than throwing — an
 * axis that renders nothing at all is worse than one tick reading `13`.
 */
export function shortMonthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-');
  const idx = Number(m) - 1;
  const yy = (y ?? '').slice(-2);
  return `${MONTH_NAMES[idx] ?? m} \u2019${yy}`;
}

/**
 * The label an axis tick should carry for a bucket.
 *
 * ⚠️ MONTHS SHORTEN, DAYS DO NOT. A daily bucket's label is already `3 Sep` —
 * short, and with no year to abbreviate. Routing both through one function is
 * what stops a caller shortening months on one chart and not the next; two date
 * formats on one report is worse than one long one.
 */
export function axisBucketLabel(point: { bucket: string; label: string }): string {
  return /^\d{4}-\d{2}$/.test(point.bucket) ? shortMonthLabel(point.bucket) : point.label;
}

// ---------------------------------------------------------------------------
// Axis tick thinning
// ---------------------------------------------------------------------------

/**
 * Width of one `.rpt-axis-label` character in the chart's USER UNITS — 10px in
 * the report's mono face with `letter-spacing: 0.06em`.
 *
 * ⚠️ MEASURED IN A BROWSER, not guessed: `Sep 2025` renders 48.8 units wide over
 * 8 characters and `Sep ’26` about 48 over 7. 6.9 is the higher of the two,
 * rounded up — erring HIGH costs one dropped tick where erring low costs the
 * collision this exists to remove.
 */
const AXIS_CHAR_UNITS = 6.9;

/**
 * The clear space two ticks need between them, IN ON-SCREEN PIXELS.
 *
 * ⚠️⚠️ PIXELS, WHILE EVERYTHING ELSE HERE IS USER UNITS, AND THAT ASYMMETRY IS
 * THE WHOLE MECHANISM. Measured in a browser: the labels in Scott's screenshot
 * were NOT overlapping — `Sep 2025` is 48.8 units in a 54.2-unit band, so there
 * were 5.4 units of clearance. On a full-width chart those 5.4 units are ~7px
 * and read as a gap; on a 330px CRM card the SAME 5.4 units are ~2px and read as
 * one continuous run of text. Legibility is a property of the rendered pixel,
 * not of the coordinate system, so this one number cannot be expressed in user
 * units without over-thinning every wide chart to fix the narrow ones.
 */
const AXIS_LABEL_GAP_PX = 8;

/**
 * How many buckets to skip between axis labels.
 *
 * ⚠️⚠️ DERIVED FROM THE WIDEST LABEL ACTUALLY BEING DRAWN AND THE RENDERED
 * SCALE, not from a band-width threshold. The thresholds this replaces (26 / 12
 * user units) were tuned against a full-width chart; the same rule on a card
 * half the width produced `Sep 2025Oct 2025Nov 2025…`.
 *
 * ⚠️ THE SHORT MONTH FORM ALONE WOULD NOT HAVE FIXED IT — MEASURED: `Sep ’25` is
 * only 13% narrower than `Sep 2025` (42.7px against 48.8px at 10px). Shortening
 * postpones the collision, thinning removes it, and they are separate changes
 * for that reason.
 *
 * ⚠️ `scale` is rendered width ÷ viewBox width. It DEFAULTS TO 1 so a caller
 * that cannot measure still gets the old, correct-at-full-width answer rather
 * than a division by an undefined.
 *
 * ⚠️ Returns at least 1 — a stride of 0 would render nothing and divide by zero.
 */
export function labelStride(
  points: ReadonlyArray<{ bucket: string; label: string }>,
  band: number,
  scale = 1,
): number {
  if (points.length === 0 || !Number.isFinite(band) || band <= 0) return 1;
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const widest = points.reduce((w, p) => Math.max(w, axisBucketLabel(p).length), 0);
  const neededPx = widest * AXIS_CHAR_UNITS * s + AXIS_LABEL_GAP_PX;
  return Math.max(1, Math.ceil(neededPx / (band * s)));
}

export function dayLabel(dateYmd: string): string {
  const [, m, d] = dateYmd.split('-');
  const idx = Number(m) - 1;
  return `${Number(d)} ${MONTH_NAMES[idx] ?? m}`;
}

/**
 * Every day from `from` to `to` inclusive. String arithmetic via UTC Date so a
 * browser timezone can never shift a bucket by a day.
 */
export function enumerateDays(from: string, to: string): string[] {
  const out: string[] = [];
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return out;
  for (let t = start; t <= end; t += 86_400_000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

/** Every month key from the month of `from` to the month of `to`, inclusive. */
export function enumerateMonths(from: string, to: string): string[] {
  const out: string[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  const endY = Number(to.slice(0, 4));
  const endM = Number(to.slice(5, 7));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return out;
  let guard = 0;
  while ((y < endY || (y === endY && m <= endM)) && guard < 600) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    guard += 1;
  }
  return out;
}

/**
 * The 13-month window ending in the month of `today` — twelve complete months
 * plus the current, partial one.
 */
export function thirteenMonthRange(today: string): { from: string; to: string } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  // 12 months back from the current month.
  const totalMonths = y * 12 + (m - 1) - 12;
  const fromY = Math.floor(totalMonths / 12);
  const fromM = (totalMonths % 12) + 1;
  return { from: `${fromY}-${String(fromM).padStart(2, '0')}-01`, to: today };
}

// ---------------------------------------------------------------------------
// Coverage → per-bucket state
// ---------------------------------------------------------------------------

/**
 * Decide what a bucket's coverage state is, BEFORE any number is looked at.
 *
 * ⚠️ The order of these branches is the honesty ordering, and it matters:
 *   1. Nothing measured at all for this scope → EVERY bucket is `no-data`. An
 *      account with no history must not render thirteen zero bars.
 *   2. The bucket ends before measurement began → `no-data`.
 *   3. The bucket is not finished yet → `in-progress`.
 *   4. The bucket straddles the start of measurement, or sits in the window
 *      where only SOME accounts in scope were covered → `partial`.
 *   5. Otherwise → `complete`.
 *
 * `bucketStart` / `bucketEnd` are inclusive YYYY-MM-DD.
 */
export function bucketCoverage(
  bucketStart: string,
  bucketEnd: string,
  coverage: PortalCoverage,
  today: string,
): PortalBucketCoverage {
  // (1) Nothing has ever been measured for this scope on this channel.
  if (!coverage.firstDate) return 'no-data';
  // (2) The whole bucket predates the first measured day.
  if (bucketEnd < coverage.firstDate) return 'no-data';
  // (3) Not finished yet — the current month/day.
  if (bucketEnd >= today) return 'in-progress';
  // (4a) The bucket contains the first measured day, so part of it is unmeasured.
  if (bucketStart < coverage.firstDate) return 'partial';
  // (4b) Multi-account scope: some accounts were not covered yet in this bucket,
  // so the aggregate is real but understates.
  if (coverage.fullFromDate && bucketStart < coverage.fullFromDate) return 'partial';
  return 'complete';
}

/** First / last inclusive day of a bucket key ('YYYY-MM' or 'YYYY-MM-DD'). */
export function bucketBounds(bucket: string): { start: string; end: string } {
  if (bucket.length === 10) return { start: bucket, end: bucket };
  const y = Number(bucket.slice(0, 4));
  const m = Number(bucket.slice(5, 7));
  // Day 0 of the NEXT month is the last day of this one.
  const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  return { start: `${bucket}-01`, end: last };
}

// ---------------------------------------------------------------------------
// CPL
// ---------------------------------------------------------------------------

/**
 * ⚠️ RE-DERIVED, ALWAYS. Cost per lead is spend ÷ leads for THIS bucket — never
 * the mean of the daily CPLs inside it, which answers a different question and
 * gets the wrong number whenever daily volume varies (i.e. always).
 *
 * ⚠️⚠️ SPEND WITH NO LEADS RETURNS NULL, NOT 0 — and this is the single most
 * dangerous place in the report to get wrong. A CPL of zero reads as "leads were
 * free"; the truth is the exact opposite, money spent and nothing returned. On a
 * client's report that is the most FLATTERING possible misstatement, which is
 * the worst direction to be wrong in. Mutation-checked from both sides.
 *
 * Zero spend and zero leads is also null — nothing happened, and 0 would draw a
 * line along the axis as though it were a measurement.
 */
export function deriveCostPerLead(spend: number, leads: number): number | null {
  if (leads <= 0) return null;
  return round2(spend / leads);
}

/**
 * Cost per click — the same shape as its twin above, and deliberately beside it.
 *
 * ⚠️⚠️ DO NOT INLINE `spend / clicks` AT A CALL SITE. A ratio defined in one
 * place and re-derived in another is the duplicated-helper pattern that produced
 * the `toNum` incident: the shared version was right and the local copy was not,
 * and nothing failed until a quarter's spend had been rendered as zero.
 *
 * ⚠️ NO CLICKS RETURNS NULL, NOT 0, for the same reason `deriveCostPerLead` does.
 * A search term with impressions and no clicks has no cost per click; a $0.00
 * cost per click is a claim that clicks were free, which is the flattering
 * direction to be wrong in on a client's report.
 */
export function deriveCostPerClick(spend: number, clicks: number): number | null {
  if (clicks <= 0) return null;
  return round2(spend / clicks);
}

/**
 * ⚠️⚠️ TWO CAUSES PRODUCE A NULL CPL, AND THEY MEAN OPPOSITE THINGS.
 *
 *   · UNMEASURED (`no-data`) — we know nothing about those days. The line must
 *     BREAK: drawing across them is a confident claim about a period we never
 *     measured.
 *   · SPENT, NO LEADS — a fully KNOWN state. Breaking the line here looks like a
 *     rendering bug (Scott's report), because the reader can see we have data
 *     either side. The line should CONNECT ACROSS it, with the point itself
 *     marked so the truth is available on hover.
 *
 * Both stay NULL as a VALUE — the connection is a drawing decision, never a
 * substituted figure. This predicate is what tells the two apart, and it is the
 * only thing that may.
 */
export function isSpendWithoutLeads(point: PortalSeriesPoint): boolean {
  if (point.coverage === 'no-data') return false;
  if (point.costPerLead !== null) return false;
  return (point.spend ?? 0) > 0;
}

/**
 * Split a series into the runs a chart may draw a continuous line through.
 *
 * A segment ends at an UNMEASURED bucket and only there. Within a segment the
 * chart connects across nulls (so a spent-no-leads day joins up); between
 * segments nothing is drawn at all.
 *
 * ⚠️ This is how one chart gets both behaviours, which a single `connectNulls`
 * boolean cannot express — it is all-or-nothing across every null in the series.
 * Returns inclusive index ranges into the original array.
 */
export function connectableSegments(
  series: readonly PortalSeriesPoint[],
): Array<{ from: number; to: number }> {
  const segments: Array<{ from: number; to: number }> = [];
  let start: number | null = null;
  series.forEach((point, i) => {
    if (point.coverage === 'no-data') {
      if (start !== null) {
        segments.push({ from: start, to: i - 1 });
        start = null;
      }
      return;
    }
    if (start === null) start = i;
  });
  if (start !== null) segments.push({ from: start, to: series.length - 1 });
  return segments;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * The earlier of two freshness dates — a figure combining two sources is only as
 * fresh as its stalest input.
 *
 * ⚠️ SHARED, not re-derived per caller. It was local to the portal's report
 * assembly until the CRM panel needed the same rule across two GymSales tables;
 * a second three-line copy is exactly the duplicated-helper pattern that produced
 * the `toNum` incident, where the shared version was right, the local copy was
 * not, and nothing failed until a quarter's spend had been rendered as zero.
 *
 * ⚠️ A null input is "we could not read this one", NOT "the beginning of time" —
 * so it yields to the other rather than making the answer null. Two nulls stay
 * null, which the copy turns into "we could not read when this was pulled".
 */
export function olderAsOf(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}

// ---------------------------------------------------------------------------
// Series assembly
// ---------------------------------------------------------------------------

export interface BuildSeriesOptions {
  /** Inclusive range the chart spans. */
  from: string;
  to: string;
  grouping: PortalGrouping;
  /** Measured rows, keyed by day (YYYY-MM-DD). Missing days are absent. */
  measured: readonly MeasuredBucket[];
  coverage: PortalCoverage;
  /** The client's today (YYYY-MM-DD), so the in-progress bucket is theirs. */
  today: string;
}

/**
 * Build a continuous series over the range: every bucket present, each carrying
 * either real figures or an explicit gap.
 *
 * ⚠️ A bucket with no measured row is NOT automatically a gap. Inside the
 * covered window, no row means the account genuinely spent nothing that day — a
 * real zero, and saying so is accurate. Outside it, no row means we were not
 * measuring. `bucketCoverage` is what tells the two apart, and it is the only
 * thing that may.
 */
export function buildSeries(opts: BuildSeriesOptions): PortalSeriesPoint[] {
  const byDay = new Map<string, MeasuredBucket>();
  for (const row of opts.measured) {
    const existing = byDay.get(row.bucket);
    if (existing) {
      existing.leads += row.leads;
      existing.spend += row.spend;
      existing.impressions += row.impressions;
      existing.clicks += row.clicks;
    } else {
      byDay.set(row.bucket, { ...row });
    }
  }

  const keys =
    opts.grouping === 'daily'
      ? enumerateDays(opts.from, opts.to)
      : enumerateMonths(opts.from, opts.to);

  return keys.map((key) => {
    const { start, end } = bucketBounds(key);
    const coverage = bucketCoverage(start, end, opts.coverage, opts.today);
    const label = opts.grouping === 'daily' ? dayLabel(key) : monthLabel(key);

    if (coverage === 'no-data') {
      // ⚠️ NULL, not 0 — on EVERY figure including the delivery counts. See the
      // file header; a sparkline that drew an unmeasured month as zero would be
      // the same lie in a smaller frame.
      return {
        bucket: key,
        label,
        coverage,
        leads: null,
        spend: null,
        costPerLead: null,
        impressions: null,
        clicks: null,
      };
    }

    // Sum the measured days that fall inside this bucket.
    let leads = 0;
    let spend = 0;
    let impressions = 0;
    let clicks = 0;
    if (opts.grouping === 'daily') {
      const row = byDay.get(key);
      if (row) {
        leads = row.leads;
        spend = row.spend;
        impressions = row.impressions;
        clicks = row.clicks;
      }
    } else {
      for (const [day, row] of byDay) {
        if (monthKeyOf(day) !== key) continue;
        leads += row.leads;
        spend += row.spend;
        impressions += row.impressions;
        clicks += row.clicks;
      }
    }

    return {
      bucket: key,
      label,
      coverage,
      leads: round2(leads),
      spend: round2(spend),
      costPerLead: deriveCostPerLead(spend, leads),
      // ⚠️ Counts, so no rounding — and a measured zero stays 0, which is a real
      // fact about a month that ran nothing.
      impressions,
      clicks,
    };
  });
}

/**
 * Sum a set of measured rows into period totals, per currency.
 *
 * ⚠️ Per-currency, never blended. A client sees their own currency unconverted;
 * the agency display-currency machinery is an internal cross-portfolio tool and
 * must not appear here. In the practically-universal single-currency case this
 * returns exactly one entry.
 */
export function buildTotals(
  rows: readonly { currency: string; leads: number; spend: number }[],
): PortalTotals[] {
  const acc = new Map<string, { leads: number; spend: number }>();
  for (const row of rows) {
    const cur = acc.get(row.currency) ?? { leads: 0, spend: 0 };
    cur.leads += row.leads;
    cur.spend += row.spend;
    acc.set(row.currency, cur);
  }
  return [...acc.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([currency, v]) => ({
      currency,
      leads: round2(v.leads),
      spend: round2(v.spend),
      costPerLead: deriveCostPerLead(v.spend, v.leads),
    }));
}

/**
 * Merge two channels' coverage into the combined block's coverage.
 *
 * ⚠️ The COMBINED chart can only claim to be complete where BOTH channels are.
 * `firstDate` is the earlier of the two (something was measured from then), and
 * `fullFromDate` the LATER (only from then is the combined figure whole) — so a
 * client running Meta for two years and Google for three months sees the early
 * months flagged partial rather than presented as a full picture that silently
 * omits Google.
 */
export function combineCoverage(a: PortalCoverage, b: PortalCoverage): PortalCoverage {
  const firstDates = [a.firstDate, b.firstDate].filter((d): d is string => !!d);
  const fullDates = [a.fullFromDate, b.fullFromDate].filter((d): d is string => !!d);
  return {
    firstDate: firstDates.length ? firstDates.reduce((x, y) => (x < y ? x : y)) : null,
    fullFromDate: fullDates.length ? fullDates.reduce((x, y) => (x > y ? x : y)) : null,
    accountsWithData: Math.max(a.accountsWithData, b.accountsWithData),
    accountsInScope: Math.max(a.accountsInScope, b.accountsInScope),
  };
}

/**
 * The one-line explanation shown under a chart that contains gaps. Returns null
 * when there is nothing to explain, so the note never appears on a clean chart.
 *
 * ⚠️ It says what we DO know ("reporting for this account starts in March 2026")
 * rather than an apology. A client reading a gap needs to understand it is an
 * absence of measurement, not an absence of results.
 */
export function coverageNote(
  series: readonly PortalSeriesPoint[],
  coverage: PortalCoverage,
): string | null {
  const hasGap = series.some((p) => p.coverage === 'no-data');
  const hasPartial = series.some((p) => p.coverage === 'partial');
  if (!hasGap && !hasPartial) return null;

  if (!coverage.firstDate) {
    return 'We have no reporting data for this period yet, so these months are shown as gaps rather than zero.';
  }
  const from = monthLabel(monthKeyOf(coverage.firstDate));
  const parts: string[] = [];
  if (hasGap) {
    parts.push(
      `Reporting for this account starts in ${from}. Earlier months are shown as gaps because there is no data for them — not because there was no activity.`,
    );
  }
  if (hasPartial) {
    parts.push(
      coverage.accountsInScope > 1
        ? 'Months marked partial were not yet being reported for every location, so those totals are lower than the full picture.'
        : 'Months marked partial were only reported for part of the month.',
    );
  }
  return parts.join(' ');
}
